import { useEffect, useState } from 'react';
import { getWeatherHistory, type HistoricalDayRow } from '../api/weather.js';
import { DataCard } from './ui/data-card';
import { StatTile } from './ui/stat-tile';

interface Props {
  courseSlug: string;
}

function isoDate(d: Date): string {
  return d.toISOString().slice(0, 10);
}
function shift(iso: string, days: number): string {
  const d = new Date(iso + 'T00:00:00Z');
  d.setUTCDate(d.getUTCDate() + days);
  return isoDate(d);
}

function yearStart(): string {
  const now = new Date();
  return `${now.getUTCFullYear()}-01-01`;
}

export function GrowthMetricsCard({ courseSlug }: Props) {
  const [last7, setLast7] = useState<HistoricalDayRow[] | null>(null);
  const [ytd, setYtd] = useState<HistoricalDayRow[] | null>(null);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const today = isoDate(new Date());
        const from7 = shift(today, -7);
        const [recent, year] = await Promise.all([
          getWeatherHistory(courseSlug, from7, today),
          getWeatherHistory(courseSlug, yearStart(), today),
        ]);
        if (!cancelled) {
          setLast7(recent.days);
          setYtd(year.days);
        }
      } catch (err) {
        if (!cancelled) setError(err instanceof Error ? err.message : String(err));
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [courseSlug]);

  // Hide entirely when no data yet (new course). The cron backfills daily.
  if (error) return null;
  if (!last7 || !ytd) return null;

  const sum7Et0 = sumField(last7, 'et0In');
  const sum7Rain = sumField(last7, 'precipitationIn');
  const sumYtdGdd50 = sumField(ytd, 'gddBase50');
  const sumYtdGdd60 = sumField(ytd, 'gddBase60');

  // Suppress the whole card if the cron hasn't landed any ET₀/GDD yet.
  const hasAnyData = sum7Et0 !== null || sumYtdGdd50 !== null || sumYtdGdd60 !== null;
  if (!hasAnyData) return null;

  return (
    <DataCard
      className="mt-6"
      title="Growth metrics"
      subtitle={`Daily refresh · ${last7.length} recent day${last7.length === 1 ? '' : 's'}`}
    >
      <div className="grid grid-cols-2 gap-3 md:grid-cols-4">
        <StatTile
          label="7d ET₀"
          value={`${fmt(sum7Et0, 2)} in`}
          detail="FAO-56 PM reference ET"
        />
        <StatTile
          label="7d rainfall"
          value={`${fmt(sum7Rain, 2)} in`}
          detail="Open-Meteo archive"
        />
        <StatTile
          label="YTD GDD₅₀"
          value={`${fmt(sumYtdGdd50, 0)} °F`}
          detail="Cool-season pacing"
        />
        <StatTile
          label="YTD GDD₆₀"
          value={`${fmt(sumYtdGdd60, 0)} °F`}
          detail="Warm-season pacing"
        />
      </div>
    </DataCard>
  );
}

function sumField<K extends keyof HistoricalDayRow>(
  rows: HistoricalDayRow[],
  key: K,
): number | null {
  let total = 0;
  let any = false;
  for (const r of rows) {
    const v = r[key] as number | null | undefined;
    if (typeof v === 'number' && Number.isFinite(v)) {
      total += v;
      any = true;
    }
  }
  return any ? total : null;
}

function fmt(v: number | null, places: number): string {
  if (v === null) return '—';
  return v.toFixed(places);
}
